import React, { useEffect } from 'react';
import { Container, Typography, Box, Paper, Grid, Select, MenuItem, Stack, List, ListItem, Divider } from '@mui/material';
import DateRangePicker from '../components/DateRangePicker';
import { useTheme, useMediaQuery } from '@mui/material';
import { useDateRange } from '../context/DateContext';
import { useSelectedChatbot } from '../context/SelectedChatbotContext';
import { fetchData, getImpressions } from '../services/api';
import {
  PieChart, Pie, Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
  LineChart,
  Line
} from 'recharts';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#4fc3f7', '#81c784', '#ff8a65', '#ba68c8', '#fff176', '#90a4ae', '#f06292', '#7986cb'];

const Analytics = () => {
  const theme = useTheme();
  const { startDate, endDate, setStartDate, setEndDate } = useDateRange();
  const { selectedChatbot } = useSelectedChatbot();
  const [wordLimit, setWordLimit] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);
  const [impressions, setImpressions] = React.useState([]);
  const [totalImpressions, setTotalImpressions] = React.useState(0);
  const [noDataAvailable, setNoDataAvailable] = React.useState(false);
  const [keywordData, setKeywordData] = React.useState([]);
  const [intentionData, setIntentionData] = React.useState([]);
  const [patternData, setPatternData] = React.useState([]);
  const [contactData, setContactData] = React.useState([]);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleKeywordClick = (keyword) => {
    navigate(`/conversations?keyword=${encodeURIComponent(keyword)}`);
  };

  const handleIntentClick = (entry) => {
    if (entry && entry.name) {
      navigate(`/conversations?intent=${encodeURIComponent(entry.name)}`);
    }
  };

  const handlePatternClick = (pattern) => {
    if (pattern && pattern.sequence) {
      navigate(`/conversations?pattern=${encodeURIComponent(pattern.sequence)}`);
    }
  };

  const handleContactClick = (type) => {
    const contactTypeToField = {
      'Email': 'email',
      'Teléfono': 'phone',
      'Nombre': 'name',
      'Empresa': 'company',
      'Referencias': 'references'
    };
    const field = contactTypeToField[type];
    if (field) {
      navigate(`/conversations?contact=${encodeURIComponent(field)}`);
    }
  };

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      if (!selectedChatbot) return;

      // Clear previous data when switching assistants
      setImpressions([]);
      setTotalImpressions(0);
      setKeywordData([]);
      setIntentionData([]);
      setPatternData([]);
      setContactData([]);
      setNoDataAvailable(false);

      setIsLoading(true);
      setApiError(null);
      try {
        // Fetch impressions
        const impressionsData = await getImpressions(
          selectedChatbot,
          startDate.format('YYYYMMDD'),
          endDate.format('YYYYMMDD')
        );

        // Check if impressionsData is valid
        if (!impressionsData || !Array.isArray(impressionsData) || impressionsData.length === 0) {
          // If no impressions data, try to fetch other data before showing no data message
          const response = await fetchData(
            1, // tipo
            selectedChatbot,
            startDate.format('YYYYMMDD'),
            endDate.format('YYYYMMDD')
          );

          // If both impressions and response data are empty, show no data message
          if (!response || !Array.isArray(response) || response.length === 0) {
            setNoDataAvailable(true);
            return;
          }

          // If we have response data but no impressions, set empty impressions
          setImpressions([]);
          setTotalImpressions(0);
        } else {
          // Format data for chart and calculate total
          const formattedImpressions = impressionsData.map(item => ({
            date: item.date.toString(),
            total: item.total
          }));

          const total = impressionsData.reduce((sum, item) => sum + item.total, 0);

          setImpressions(formattedImpressions);
          setTotalImpressions(total);
        }

        // Fetch conversation data
        const response = await fetchData(
          1, // tipo
          selectedChatbot,
          startDate.format('YYYYMMDD'),
          endDate.format('YYYYMMDD')
        );

        // Check if there's no data available (both impressions and response)
        if ((!impressionsData || impressionsData.length === 0) && (!response || response.length === 0)) {
          setNoDataAvailable(true);
          return;
        }

        // Procesar palabras clave (tags)
        const tagsCount = {};
        if (response && response.length > 0) {
          response.forEach(chat => {
            if (chat.tags) {
              const tags = chat.tags.split(',');
              tags.forEach(tag => {
                // Capitalizar la primera letra de cada palabra
                const cleanTag = tag.trim().split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');
                tagsCount[cleanTag] = (tagsCount[cleanTag] || 0) + 1;
              });
            }
          });
        }
        const keywordDataProcessed = Object.entries(tagsCount)
          .map(([word, count]) => ({ word, count }))
          .sort((a, b) => b.count - a.count);
        setKeywordData(keywordDataProcessed);

        // Procesar intenciones
        const intentionsCount = {};
        if (response && response.length > 0) {
          response.forEach(chat => {
            if (chat.intencion) {
              const intention = chat.intencion.trim();
              intentionsCount[intention] = (intentionsCount[intention] || 0) + 1;
            }
          });
        }
        const intentionDataProcessed = Object.entries(intentionsCount)
          .map(([name, value]) => ({ name, value }));
        setIntentionData(intentionDataProcessed);

        // Procesar patrones de consulta
        const patternsCount = {};
        if (response && response.length > 0) {
          response.forEach(chat => {
            if (chat.patron) {
              const pattern = chat.patron.trim();
              patternsCount[pattern] = (patternsCount[pattern] || 0) + 1;
            }
          });
        }
        const totalPatterns = Object.values(patternsCount).reduce((a, b) => a + b, 0) || 0;
        const patternDataProcessed = Object.entries(patternsCount)
          .map(([sequence, count]) => ({
            sequence,
            count,
            percentage: totalPatterns > 0 ? Math.round((count / totalPatterns) * 100) : 0
          }))
          .sort((a, b) => b.count - a.count);
        setPatternData(patternDataProcessed);

        // Procesar información de contacto (asegurando que siempre haya valores)
        const contactFields = {
          'Email': response && response.length > 0 ? response.filter(chat => chat.email).length || 0 : 0,
          'Teléfono': response && response.length > 0 ? response.filter(chat => chat.telefono).length || 0 : 0,
          'Nombre': response && response.length > 0 ? response.filter(chat => chat.nombre).length || 0 : 0,
          'Empresa': response && response.length > 0 ? response.filter(chat => chat.empresa).length || 0 : 0,
          'Referencias': response && response.length > 0 ? response.filter(chat => chat.referencias).length || 0 : 0
        };
        const contactDataProcessed = Object.entries(contactFields)
          .map(([type, count]) => ({ type, count }));
        setContactData(contactDataProcessed);

      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [startDate, endDate, selectedChatbot]);

  const maxCount = Math.max(...keywordData.map(item => item.count));

  return (
    <Container maxWidth={false} sx={{ py: 0, backgroundColor: 'transparent', minHeight: '100vh' }}>
      <Box sx={{ py: 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: { xs: 2.5, sm: 3.5 },
            borderRadius: 1,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 2.5,
          }}
        >
          <Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ color: theme.palette.text.primary, mb: 0.5, fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', }}>
              Analíticas
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500, fontSize: '1rem', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', }}>
              Aquí puedes ver análisis del contenido de las conversaciones del AI4Assistant
            </Typography>
          </Box>
          <Box>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />
          </Box>
        </Paper>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Cargando datos...
          </Typography>
        </Box>
      )}

      {apiError && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Typography variant="h5" sx={{ color: '#ff5252', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
            Error: {apiError}
          </Typography>
        </Box>
      )}

      {noDataAvailable && !isLoading && !apiError && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          my: 4,
          p: 4,
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.action.hover
        }}>
          <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', textAlign: 'center' }}>
            No hay registros de analíticas para el período seleccionado.<br />
            Seleccione otro período o asistente por favor.
          </Typography>
        </Box>
      )}

      {!isLoading && !apiError && !noDataAvailable && (
        <Grid container spacing={3}>
          {/* Tabla de palabras clave */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: theme.palette.text.primary, flex: 1, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                  Palabras Clave Más Frecuentes
                </Typography>
                <Select
                  value={wordLimit}
                  onChange={(e) => setWordLimit(e.target.value)}
                  size="small"
                  sx={{
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '& .MuiSvgIcon-root': {
                      color: theme.palette.text.primary,
                    },
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  }}
                >
                  <MenuItem value={5} sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Top 5</MenuItem>
                  <MenuItem value={10} sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Top 10</MenuItem>
                  <MenuItem value={20} sx={{ color: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>Top 20</MenuItem>
                </Select>
              </Stack>

              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {keywordData.slice(0, wordLimit).map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        '& .keyword-bar': {
                          bgcolor: theme.palette.primary.light,
                        },
                        '& .keyword-text': {
                          color: theme.palette.primary.light,
                        }
                      }
                    }}
                    onClick={() => handleKeywordClick(item.word)}
                  >
                    <Typography
                      className="keyword-text"
                      sx={{
                        minWidth: '120px',
                        color: theme.palette.text.primary,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        transition: 'color 0.2s'
                      }}
                    >
                      {item.word}
                    </Typography>
                    <Box sx={{
                      flex: 1,
                      height: '24px',
                      position: 'relative',
                      ml: 2
                    }}>
                      <Box
                        className="keyword-bar"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          height: '100%',
                          width: `${(item.count / maxCount) * 100}%`,
                          bgcolor: theme.palette.primary.main,
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          pl: 1,
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <Typography sx={{
                          color: theme.palette.primary.contrastText,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                        }}>
                          {item.count}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Gráfico de Intención del Usuario */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
              }}
            >
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Intención del Usuario
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={intentionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      onClick={handleIntentClick}
                      cursor="pointer"
                    >
                      {intentionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend
                      formatter={(value, entry) => (
                        <span
                          style={{
                            color: theme.palette.text.primary,
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleIntentClick(entry.payload)}
                        >
                          {value}
                        </span>
                      )}
                    />
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const index = intentionData.findIndex(item => item.name === data.name);
                          const color = COLORS[index % COLORS.length];
                          return (
                            <Box sx={{
                              bgcolor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.primary.main}`,
                              p: 1,
                              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                            }}>
                              <Typography sx={{
                                color: color,
                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                fontSize: '0.875rem'
                              }}>
                                {data.name}: {data.value}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Patrones de Consulta */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                height: '100%',
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Patrones de Consulta
              </Typography>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {patternData.map((pattern, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <Box sx={{ width: '100%' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              '& .pattern-text, & .pattern-count': {
                                color: theme.palette.primary.main
                              },
                              '& .pattern-bar': {
                                bgcolor: theme.palette.primary.light
                              }
                            }
                          }}
                          onClick={() => handlePatternClick(pattern)}
                        >
                          <Typography
                            className="pattern-text"
                            sx={{
                              color: theme.palette.text.primary,
                              fontSize: '0.9rem',
                              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                              transition: 'color 0.2s'
                            }}
                          >
                            {pattern.sequence}
                          </Typography>
                          <Typography
                            className="pattern-count"
                            sx={{
                              color: theme.palette.primary.main,
                              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                              transition: 'color 0.2s'
                            }}
                          >
                            {pattern.count} casos
                          </Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: '8px', bgcolor: theme.palette.divider, borderRadius: '4px', overflow: 'hidden' }}>
                          <Box
                            className="pattern-bar"
                            sx={{
                              width: `${pattern.percentage}%`,
                              height: '100%',
                              bgcolor: theme.palette.primary.main,
                              borderRadius: '4px',
                              transition: 'background-color 0.2s'
                            }}
                          />
                        </Box>
                        <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem', mt: 0.5, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                          {pattern.percentage}% de las conversaciones
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < patternData.length - 1 && (
                      <Divider sx={{ borderColor: theme.palette.divider, my: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Impresiones Section */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: '100%',
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                }}
              >
                Impresiones del asistente
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  mb: 3,
                  color: theme.palette.primary.main,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  textAlign: 'center'
                }}
              >
                {totalImpressions}
              </Typography>

              <Box sx={{ width: '100%', height: 250, mt: 2 }}>
                <ResponsiveContainer>
                  <LineChart
                    data={impressions}
                    style={{ backgroundColor: theme.palette.background.default }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.secondary}
                      tickFormatter={(value) => {
                        const date = value.toString();
                        return `${date.slice(6, 8)}/${date.slice(4, 6)}`;
                      }}
                    />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px'
                      }}
                      labelStyle={{ color: theme.palette.text.secondary }}
                      itemStyle={{ color: theme.palette.primary.main }}
                      formatter={(value) => [`${value} impresiones`]}
                      labelFormatter={(value) => {
                        const date = value.toString();
                        return `${date.slice(6, 8)}/${date.slice(4, 6)}/${date.slice(0, 4)}`;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.primary.main, r: 4 }}
                      activeDot={{ r: 6, fill: theme.palette.primary.contrastText }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Información de Contacto */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
                Información de Contacto Proporcionada
              </Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <ResponsiveContainer width="95%" height="100%">
                  <BarChart
                    data={contactData}
                    layout="horizontal"
                    margin={{ top: 5, right: 5, left: -40, bottom: 5 }}
                    barSize={55}
                    barGap={0}
                    maxBarSize={50}
                  >
                    <YAxis
                      type="number"
                      axisLine={false}
                      tick={{ fill: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      tickLine={false}
                    />
                    <XAxis
                      dataKey="type"
                      type="category"
                      tick={{ fill: theme.palette.text.primary, fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
                      interval={0}
                      height={50}
                    />
                    <CartesianGrid strokeDasharray="0 0" stroke={theme.palette.divider} />
                    <RechartsTooltip
                      cursor={{ fill: theme.palette.text.secondary, opacity: 0.1 }}
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.text.secondary}`,
                        borderRadius: '4px',
                        padding: '8px',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                      }}
                      labelStyle={{
                        color: theme.palette.text.secondary,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontSize: '0.875rem'
                      }}
                      itemStyle={{
                        color: theme.palette.text.primary,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                        fontSize: '0.875rem'
                      }}
                      formatter={(value, name) => [`${value}`, 'Cantidad']}
                      labelFormatter={(label) => `Tipo: ${label}`}
                    />
                    <Bar
                      dataKey="count"
                      minPointSize={3}
                      onClick={(data) => handleContactClick(data.type)}
                      cursor="pointer"
                    >
                      {contactData.map((entry, index) => (
                        <Cell
                          key={`cell-${entry.type}`}
                          fill={COLORS[index % COLORS.length]}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default Analytics;
